import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Presentation, Loader2, Download, FileText } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { generateConferenceReport, ReportMode, ReportScope, ReportFormat } from "@/lib/generateConferenceReport";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";

interface Props {
  isGlobalAdmin: boolean;
  userRegion?: string;
  defaultYear?: number;
}

interface ArchivedReport {
  id: string;
  conference_year: number;
  scope: string;
  region: string | null;
  mode: string;
  format: string;
  file_name: string;
  public_url: string | null;
  president_name: string | null;
  secretary_name: string | null;
  meeting_date: string | null;
  generated_by_name: string | null;
  created_at: string;
}

// Load logo from /ofuesi-logo.png as base64 data URL (for embedding in pptx/pdf)
async function loadLogoDataUrl(): Promise<string | undefined> {
  try {
    const res = await fetch("/ofuesi-logo.png");
    if (!res.ok) return undefined;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onerror = reject;
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(blob);
    });
  } catch { return undefined; }
}

export const ConsolidatedReportButton: React.FC<Props> = ({ isGlobalAdmin, userRegion, defaultYear }) => {
  const { adminUser } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<ReportScope>(isGlobalAdmin ? "nation" : "region");
  const [mode, setMode] = useState<ReportMode>("summary");
  const [format, setFormat] = useState<ReportFormat>("pptx");
  const [year, setYear] = useState<number>(defaultYear || new Date().getFullYear());
  const [presidentName, setPresidentName] = useState("");
  const [secretaryName, setSecretaryName] = useState("");
  const [meetingDate, setMeetingDate] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [archive, setArchive] = useState<ArchivedReport[]>([]);
  const [loadingArchive, setLoadingArchive] = useState(false);

  const loadArchive = async () => {
    setLoadingArchive(true);
    let q = (supabase as any).from("conference_report_archive").select("*").order("created_at", { ascending: false }).limit(50);
    if (scope === "region" && userRegion) q = q.eq("region", userRegion);
    if (scope === "nation") q = q.eq("scope", "nation");
    const { data } = await q;
    setArchive((data as ArchivedReport[]) || []);
    setLoadingArchive(false);
  };

  useEffect(() => { if (open) loadArchive(); /* eslint-disable-next-line */ }, [open, scope, year]);

  const run = async () => {
    if (scope === "region" && !userRegion) {
      toast({ title: "No region", description: "Your account has no region assigned.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const logoDataUrl = await loadLogoDataUrl();
      const result = await generateConferenceReport({
        scope, mode, year, region: userRegion, format,
        cover: { presidentName, secretaryName, meetingDate, logoDataUrl },
        generatedByName: adminUser?.full_name,
        generatedById: adminUser?.id,
      });
      toast({ title: "Report generated & archived", description: result.fileName });
      loadArchive();
    } catch (e: any) {
      toast({ title: "Failed to generate", description: e.message || String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Presentation className="w-4 h-4 mr-1" /> Business Report
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Consolidated Conference Report</DialogTitle>
            <DialogDescription>
              Builds a presentation-ready report (.pptx or .pdf) with cover slide, region-wise KPI charts, financials, KIT, feedback, and recommended actions. Reports are archived year-wise.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label>Scope</Label>
                <Select value={scope} onValueChange={(v) => setScope(v as ReportScope)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isGlobalAdmin && <SelectItem value="nation">Nation-wide</SelectItem>}
                    <SelectItem value="region">Region{userRegion ? ` (${userRegion})` : ""}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Detail</Label>
                <Select value={mode} onValueChange={(v) => setMode(v as ReportMode)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="summary">Summary</SelectItem>
                    <SelectItem value="detailed">Detailed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Format</Label>
                <Select value={format} onValueChange={(v) => setFormat(v as ReportFormat)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pptx">PowerPoint (.pptx)</SelectItem>
                    <SelectItem value="pdf">PDF (.pdf)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
              </div>
            </div>

            <div className="border-t pt-3">
              <p className="text-sm font-medium mb-2">Cover slide details</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>President</Label>
                  <Input value={presidentName} onChange={(e) => setPresidentName(e.target.value)} placeholder="Full name" />
                </div>
                <div>
                  <Label>Secretary</Label>
                  <Input value={secretaryName} onChange={(e) => setSecretaryName(e.target.value)} placeholder="Full name" />
                </div>
                <div>
                  <Label>Business Meeting Date</Label>
                  <Input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">OFUESI logo is included automatically on the cover.</p>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Previously generated reports</p>
                <Button variant="ghost" size="sm" onClick={loadArchive} disabled={loadingArchive}>Refresh</Button>
              </div>
              {loadingArchive ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
              ) : archive.length === 0 ? (
                <p className="text-xs text-muted-foreground">No archived reports yet for this scope.</p>
              ) : (
                <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                  {archive.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 p-2 text-sm">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {r.format === "pdf" ? <FileText className="w-3.5 h-3.5" /> : <Presentation className="w-3.5 h-3.5" />}
                          <span className="font-medium">{r.conference_year}</span>
                          <span className="text-muted-foreground">·</span>
                          <span className="capitalize">{r.scope === "nation" ? "Nation" : r.region}</span>
                          <span className="text-muted-foreground">·</span>
                          <span className="capitalize text-xs px-1.5 py-0.5 rounded bg-muted">{r.mode}</span>
                          <span className="uppercase text-xs px-1.5 py-0.5 rounded bg-muted">{r.format}</span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {new Date(r.created_at).toLocaleString()} {r.generated_by_name ? `· by ${r.generated_by_name}` : ""}
                          {r.meeting_date ? ` · meeting ${r.meeting_date}` : ""}
                        </div>
                      </div>
                      {r.public_url && (
                        <Button asChild variant="outline" size="sm">
                          <a href={r.public_url} target="_blank" rel="noreferrer" download={r.file_name}>
                            <Download className="w-3.5 h-3.5 mr-1" /> Download
                          </a>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Close</Button>
            <Button onClick={run} disabled={busy}>
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Building…</> : <>Generate {format.toUpperCase()}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
